import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, Upload, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShokhSandeshSubmitPage() {
  const [states, setStates] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', age: '', date_of_death: '', date_of_birth: '',
    village: '', city: '', state: '', family_name: '',
    message: '', submitted_by_name: '', submitted_by_mobile: ''
  });

  useEffect(() => {
    axios.get(`${API}/api/states`).then(r => setStates(r.data)).catch(() => {});
  }, []);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.date_of_death || !form.city || !form.state || !form.message || !form.submitted_by_name || !form.submitted_by_mobile || !photo) {
      setError('कृपया सभी आवश्यक फ़ील्ड भरें और फोटो अपलोड करें');
      return;
    }
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append('photo', photo);
      await axios.post(`${API}/api/shokh-sandesh/submit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'कुछ गलत हो गया');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" data-testid="shokh-success">
        <CheckCircle className="w-20 h-20 text-green-500 mb-4" />
        <h2 className="font-outfit font-bold text-2xl mb-2">शोक संदेश दर्ज हुआ</h2>
        <p className="text-gray-600 mb-6">आपका शोक संदेश अनुमोदन के बाद प्रदर्शित होगा।</p>
        <div className="flex gap-3">
          <Link to="/shokh-sandesh"><Button variant="outline" className="rounded-full">शोक संदेश देखें</Button></Link>
          <Link to="/"><Button className="rounded-full bg-gray-900">होम</Button></Link>
        </div>
      </div>
    );
  }

  const InputField = ({ label, name, type = 'text', required, placeholder, rows }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
      {rows ? (
        <textarea value={form[name]} onChange={e => setForm({...form, [name]: e.target.value})} rows={rows}
          placeholder={placeholder} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900 resize-none" data-testid={`shokh-${name}`} />
      ) : (
        <input type={type} value={form[name]} onChange={e => setForm({...form, [name]: e.target.value})}
          placeholder={placeholder} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900" data-testid={`shokh-${name}`} />
      )}
    </div>
  );

  return (
    <div className="pb-24 md:pb-12" data-testid="shokh-submit-page">
      <div className="max-w-2xl mx-auto px-4 py-6 md:py-10">
        <Link to="/shokh-sandesh" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4" />वापस
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <Heart className="w-7 h-7 text-gray-900" fill="currentColor" />
          <h1 className="font-outfit font-bold text-2xl md:text-3xl text-gray-900">शोक संदेश दर्ज करें</h1>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-xl mb-4 text-sm" data-testid="shokh-error">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">फोटो <span className="text-red-500">*</span></label>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                {photoPreview ? (
                  <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" id="shokh-photo" data-testid="shokh-photo-input" />
                <label htmlFor="shokh-photo" className="cursor-pointer inline-block px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors">
                  {photo ? 'बदलें' : 'फोटो चुनें'}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG (अधिकतम 5MB)</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField label="दिवंगत का नाम" name="name" required placeholder="पूरा नाम" />
            <InputField label="उम्र" name="age" placeholder="जैसे: 65 वर्ष" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField label="निधन तिथि" name="date_of_death" type="date" required />
            <InputField label="जन्म तिथि" name="date_of_birth" type="date" />
          </div>

          <InputField label="परिवार का नाम" name="family_name" placeholder="जैसे: शर्मा परिवार, गुप्ता कुटुंब" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField label="गाँव/मोहल्ला" name="village" placeholder="गाँव या मोहल्ला" />
            <InputField label="शहर" name="city" required placeholder="शहर" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">राज्य <span className="text-red-500">*</span></label>
              <select value={form.state} onChange={e => setForm({...form, state: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900 bg-white" data-testid="shokh-state">
                <option value="">राज्य चुनें</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <InputField label="श्रद्धांजलि संदेश" name="message" required placeholder="अपनी श्रद्धांजलि लिखें..." rows={4} />

          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">आपकी जानकारी</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="आपका नाम" name="submitted_by_name" required placeholder="आपका नाम" />
              <InputField label="मोबाइल नंबर" name="submitted_by_mobile" required placeholder="10 अंकों का मोबाइल" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-gray-900 hover:bg-gray-800 text-white mt-4" data-testid="shokh-submit-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'शोक संदेश दर्ज करें'}
          </Button>
        </form>
      </div>
    </div>
  );
}
